import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { imagepathReturn } from "../../config/formatters.ts";

interface ResultItem {
  id: number;
  title: string;
  description: string;
  file: string;
  sort_order: number;
  status: boolean;
}

export default function Results() {
  const navigate = useNavigate();
  const [results, setResults] = useState<ResultItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getResults();
  }, [page, limit, search]);

  const getResults = async () => {
    setLoading(true);
    const response = await request.post("results", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setResults(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 180,
      format: (value) => value,
    },
    {
      id: "file",
      label: "Image",
      minWidth: 80,
      format: (value) => (
        <Box
          component="img"
          src={imagepathReturn(value)}
          sx={{
            width: 40,
            height: 40,
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
      ),
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/results/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.title }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Results"
        showSearch
        searchPlaceholder="Search results..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Result",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/results/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={results}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Result - " + deleteDialog?.title}
          message="Are you sure you want to delete this result? This action cannot be undone."
          apiEndpoint={"results/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getResults();
          }}
        />
      </PageWrapper>
    </>
  );
}
